import type { CreateEntryInput, TEntry, IdentifierIntakeContextOutput } from '@schemas'

export type IntakeAssignmentContext = {
  generation: number
  isShowStaff?: boolean
  complete: boolean
  assignedOrderIndexes: number[]
  candidates: Record<string, IdentifierIntakeContextOutput['candidates'][number]['status']>
}

export type LocalIntakeAssignment = {
  entryId: string
  exhibitorId: string
  code: string
  orderIndex: number | null
  revision: number
}

export type PendingIntakeEntry = {
  id: string
  submissionId?: string
  editVersion?: number
  completionIntent?: 'create_only' | 'create_and_check_in'
  syncAttempt?: {
    id: string
    editVersion: number
    request: CreateEntryInput
    completionIntent: 'create_only' | 'create_and_check_in'
    createdEntry?: TEntry
    completed?: boolean
  }
  canReplaceCode?: boolean
  rejectedCode?: string
  exhibitor: {
    id: string
    name: string
  }
  classId: string
  className: string
  notes?: string | null
  code: string
  // A duplicate waits for staff: the person already has an active entry in this class.
  status: 'queued' | 'syncing' | 'error' | 'duplicate'
  error?: string
  clubId?: string | null
  clubName?: string | null
  existingEntryId?: string | null
}

export type RecentIntakeEntry = {
  id: string
  entryCode: string
  exhibitorId: string
  className?: string | null
  originalCode?: string | null
}

export type CheckedInExhibit = {
  exhibitId: string
  entryId: string
  entryCode: string
  classId?: string | null
  classCode?: string | null
  className?: string | null
  exhibitorId?: string | null
  exhibitorName?: string | null
  checkedInAt: string
  remappedFrom?: string | null
}

export type CheckedInExhibitInput = Omit<CheckedInExhibit, 'checkedInAt'> & {
  checkedInAt?: string
}

export type EntryIntakeState = {
  identifierOrders?: Record<string, number>
  assignmentContexts?: Record<string, IntakeAssignmentContext>
  localAssignments?: LocalIntakeAssignment[]
  revision?: number
  retiredCodes?: string[]
  lastClaimedOwner?: string | null
  availableCodes: string[]
  pendingEntries: PendingIntakeEntry[]
  tmpCounter: number
  recentEntries: RecentIntakeEntry[]
  lastClaimedCanonical: string | null
  checkedInExhibits: CheckedInExhibit[]
}

export const ENTRY_INTAKE_INITIAL_STATE: EntryIntakeState = {
  identifierOrders: {},
  assignmentContexts: {},
  localAssignments: [],
  revision: 0,
  retiredCodes: [],
  lastClaimedOwner: null,
  availableCodes: [],
  pendingEntries: [],
  tmpCounter: 0,
  recentEntries: [],
  lastClaimedCanonical: null,
  checkedInExhibits: [],
}

function isRecentEntry(value: unknown): value is RecentIntakeEntry {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<RecentIntakeEntry>
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.entryCode === 'string' &&
    typeof candidate.exhibitorId === 'string'
  )
}

function normalizeCheckedInExhibit(value: unknown): CheckedInExhibit | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Partial<CheckedInExhibit>
  if (
    typeof candidate.exhibitId !== 'string' ||
    typeof candidate.entryId !== 'string' ||
    typeof candidate.entryCode !== 'string'
  ) {
    return null
  }
  const parsedTimestamp =
    typeof candidate.checkedInAt === 'string' && !Number.isNaN(Date.parse(candidate.checkedInAt))
      ? candidate.checkedInAt
      : new Date(0).toISOString()

  return {
    exhibitId: candidate.exhibitId,
    entryId: candidate.entryId,
    entryCode: candidate.entryCode,
    classId:
      typeof candidate.classId === 'string' ? candidate.classId : (candidate.classId ?? null),
    classCode: typeof candidate.classCode === 'string' ? candidate.classCode : null,
    className: typeof candidate.className === 'string' ? candidate.className : null,
    exhibitorId:
      typeof candidate.exhibitorId === 'string' || candidate.exhibitorId === null
        ? candidate.exhibitorId
        : null,
    exhibitorName: typeof candidate.exhibitorName === 'string' ? candidate.exhibitorName : null,
    checkedInAt: parsedTimestamp,
    remappedFrom: typeof candidate.remappedFrom === 'string' ? candidate.remappedFrom : null,
  }
}

/** Entries saved before duplicate overrides were removed wait as duplicates for staff. */
function normalizePendingEntry(stored: PendingIntakeEntry): PendingIntakeEntry {
  const {
    overrideReason: _reason,
    overrideMessage: _message,
    ...entry
  } = stored as PendingIntakeEntry & { overrideReason?: unknown; overrideMessage?: unknown }
  return {
    ...entry,
    ...((entry.status as string) === 'override_required' ? { status: 'duplicate' as const } : {}),
    completionIntent:
      entry.completionIntent === 'create_and_check_in' ? 'create_and_check_in' : 'create_only',
  }
}

export function parseEntryIntakeStateFromObject(value: unknown): EntryIntakeState {
  if (!value || typeof value !== 'object') return ENTRY_INTAKE_INITIAL_STATE
  const candidate = value as Partial<EntryIntakeState> & {
    recentEntries?: unknown
    checkedInExhibits?: unknown
  }

  return {
    identifierOrders: Object.fromEntries(
      Object.entries(candidate.identifierOrders ?? {}).filter(
        ([, index]) => Number.isInteger(index) && index >= 0
      )
    ),
    assignmentContexts: Object.fromEntries(
      Object.entries(candidate.assignmentContexts ?? {}).filter(
        ([, context]) =>
          context &&
          Number.isInteger(context.generation) &&
          Array.isArray(context.assignedOrderIndexes) &&
          context.assignedOrderIndexes.every(index => Number.isInteger(index) && index >= 0) &&
          context.candidates &&
          typeof context.candidates === 'object'
      )
    ),
    localAssignments: Array.isArray(candidate.localAssignments)
      ? candidate.localAssignments.filter(
          record =>
            record &&
            typeof record.entryId === 'string' &&
            typeof record.exhibitorId === 'string' &&
            typeof record.code === 'string' &&
            Number.isInteger(record.revision) &&
            (record.orderIndex === null ||
              (Number.isInteger(record.orderIndex) && record.orderIndex >= 0))
        )
      : [],
    revision: typeof candidate.revision === 'number' ? candidate.revision : 0,
    retiredCodes: Array.isArray(candidate.retiredCodes) ? candidate.retiredCodes : [],
    lastClaimedOwner:
      typeof candidate.lastClaimedOwner === 'string' ? candidate.lastClaimedOwner : null,
    availableCodes: Array.isArray(candidate.availableCodes) ? candidate.availableCodes : [],
    pendingEntries: Array.isArray(candidate.pendingEntries)
      ? candidate.pendingEntries.map(normalizePendingEntry)
      : [],
    tmpCounter: typeof candidate.tmpCounter === 'number' ? candidate.tmpCounter : 0,
    recentEntries: Array.isArray(candidate.recentEntries)
      ? candidate.recentEntries.filter(isRecentEntry)
      : [],
    lastClaimedCanonical:
      typeof candidate.lastClaimedCanonical === 'string' ? candidate.lastClaimedCanonical : null,
    checkedInExhibits: Array.isArray(candidate.checkedInExhibits)
      ? candidate.checkedInExhibits
          .map(record => normalizeCheckedInExhibit(record))
          .filter((record): record is CheckedInExhibit => Boolean(record))
      : [],
  }
}

export function parseEntryIntakeStateFromString(serialized: string | null): EntryIntakeState {
  if (!serialized) return ENTRY_INTAKE_INITIAL_STATE
  try {
    const parsed = JSON.parse(serialized)
    return parseEntryIntakeStateFromObject(parsed)
  } catch {
    return ENTRY_INTAKE_INITIAL_STATE
  }
}
